import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Dropdown } from "antd";
import { MailOutlined, LogoutOutlined, SwapOutlined, GlobalOutlined } from "@ant-design/icons";
import { capitalizeFirstLetterOfWord } from "controllers/common";
import PasswordChange from "feature/user/user-settings/PasswordChange";
import SwitchRoleHubPopup from "feature/user/user-settings/SwitchRoleHubPopup";
import { roles } from "shared/constants/Constants";
import { useAgentAccountDetails } from "shared/hooks/common";
import { tokenManager } from "services/client/token-manager";

const ROLE_MAPPING = {
  parking: "Parking",
  procurement: "Procurement",
  admin: "Admin",
  hub_manager: "HM",
  verification: "Verification",
  onboarding: "Onboarding",
  driver: "Driver",
  driver_manager: "DM",
  recovery_agent: "Recovery",
  finance: "Finance",
  vendor: "Vendor",
  senior_driver_manager: "SDM",
  telecaller: "Telecaller",
  fleet_manager: "FM",
  fleet_general_manager: "FGM",
  procurement_admin: "Procurement Admin",
  commercial: "Commercial",
  [roles.HUB_PROCUREMENT]: "Hub Procurement",
  [roles.FLEET_EXECUTIVE]: "Fleet Executive",
  [roles.WORKSHOP_PARTNER]: "Workshop Partner",
};

function getRoleLabel(roleKey) {
  return ROLE_MAPPING[roleKey] || capitalizeFirstLetterOfWord(roleKey || "");
}

/**
 * Shared account menu (email, switch user, desk, logout) — same items as sidebar HeaderSideBar.
 * Pass `children` as the clickable trigger (e.g. name + chevron, or avatar).
 */
export default function ProfileAccountDropdown({
  handleLogout,
  email,
  userName,
  showDeskPortalMenu = false,
  onNavigateToDeskPortal,
  children,
  placement = "bottomLeft",
}) {
  const agentSlice = useSelector((state) => state.agentSlice);
  const primaryAccountId = tokenManager.getPrimaryAccountId();
  const { agentRole } = useAgentAccountDetails();
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openSwitchRoleHub, setOpenSwitchRoleHub] = useState(false);

  const isLogoutDisabled = agentSlice?.isOnCall && agentRole === roles.TELECALLING;

  const profileMenuItems = useMemo(
    () => [
      {
        key: "email",
        icon: <MailOutlined />,
        label: <div className="text-gray-800">{email}</div>,
        disabled: true,
      },
      {
        key: "switch-role",
        icon: <SwapOutlined />,
        label: "Switch User",
        disabled: !primaryAccountId,
        title: !primaryAccountId ? "Login again to enable this feature" : "",
      },
      ...(showDeskPortalMenu
        ? [
            {
              key: "desk-portal",
              icon: <GlobalOutlined />,
              label: "Desk",
            },
          ]
        : []),
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: (
          <span className={isLogoutDisabled ? "opacity-50 cursor-not-allowed" : ""}>Logout</span>
        ),
        disabled: isLogoutDisabled,
      },
    ],
    [email, primaryAccountId, showDeskPortalMenu, isLogoutDisabled]
  );

  return (
    <>
      <PasswordChange
        onClose={() => setOpenChangePassword(false)}
        open={openChangePassword}
        button={false}
        onClick={() => {}}
      />
      <SwitchRoleHubPopup open={openSwitchRoleHub} onClose={() => setOpenSwitchRoleHub(false)} />
      <Dropdown
        trigger={["click"]}
        placement={placement}
        menu={{
          items: profileMenuItems,
          onClick: ({ key }) => {
            if (key === "change-password") {
              setOpenChangePassword(true);
            }
            if (key === "switch-role") {
              setOpenSwitchRoleHub(true);
            }
            if (key === "desk-portal") {
              onNavigateToDeskPortal?.();
            }
            if (key === "logout" && !isLogoutDisabled) {
              handleLogout();
            }
          },
        }}
      >
        {children}
      </Dropdown>
    </>
  );
}

export { getRoleLabel };
